'use client'

import { useEffect, useState } from 'react'

const HOURS = {
    1: { open: 7.5, close: 16.5, label: 'Mon' },   // Monday
    2: { open: 7.5, close: 16.5, label: 'Tue' },   // Tuesday
    3: { open: 7.5, close: 16.5, label: 'Wed' },   // Wednesday
    4: { open: 7.5, close: 16.5, label: 'Thu' },   // Thursday
    5: { open: 7.5, close: 16.5, label: 'Fri' },   // Friday
    6: { open: 7.5, close: 13.5, label: 'Sat' },   // Saturday
    0: { open: null, close: null, label: 'Sun' }    // Sunday — closed
}

function getPerthParts() {
    const now = new Date()
    const fmt = new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Perth',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZoneName: 'short'
    })
    const parts = fmt.formatToParts(now)
    const out = {}
    for (const p of parts) {
        if (p.type === 'weekday') out.weekday = p.value
        if (p.type === 'hour') out.hour = parseInt(p.value, 10)
        if (p.type === 'minute') out.minute = parseInt(p.value, 10)
    }
    const wdMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
    out.dow = wdMap[out.weekday]
    out.decimal = out.hour + out.minute / 60
    return out
}

function getStatus() {
    const { dow, decimal, hour, minute } = getPerthParts()
    const today = HOURS[dow]

    if (today.open !== null && decimal >= today.open && decimal < today.close) {
        return { open: true, label: 'Available Now' }
    }

    let nextDay = dow
    let iter = 0
    while (iter < 8) {
        nextDay = (nextDay + (iter === 0 ? 1 : 1)) % 7
        iter++
        const candidate = HOURS[nextDay]
        if (candidate.open !== null) {
            const dayLabel = candidate.label
            const openHour = Math.floor(candidate.open)
            const openMin = Math.round((candidate.open - openHour) * 60)
            const timeLabel = `${openHour}:${String(openMin).padStart(2, '0')} AM`
            return { open: false, label: `Reopens ${dayLabel} ${timeLabel}` }
        }
    }
    return { open: false, label: 'Currently Closed' }
}

export default function AvailabilityBadge({ size = 'md', showIfClosed = true }) {
    const [status, setStatus] = useState(null)

    useEffect(() => {
        setStatus(getStatus())
        const id = setInterval(() => setStatus(getStatus()), 60000)
        return () => clearInterval(id)
    }, [])

    if (!status) return null
    if (!status.open && !showIfClosed) return null

    const isSmall = size === 'sm'
    const pill = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: isSmall ? '0.35rem' : '0.5rem',
        padding: isSmall ? '0.25rem 0.6rem' : '0.35rem 0.75rem',
        borderRadius: '999px',
        fontSize: isSmall ? '0.7rem' : '0.8rem',
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        background: status.open ? '#22c55e' : '#f59e0b',
        color: '#1A1A1A',
        border: '1px solid rgba(0,0,0,0.15)',
        boxShadow: status.open ? '0 2px 8px rgba(34, 197, 94, 0.45)' : '0 2px 8px rgba(245, 158, 11, 0.35)'
    }

    const dotSize = isSmall ? '0.5rem' : '0.6rem'

    return (
        <span className={`availability-badge ${status.open ? 'is-open' : 'is-closed'}`} style={pill}>
            <span className="availability-dot-wrap" aria-hidden="true">
                <span className="availability-dot" style={{ width: dotSize, height: dotSize }} />
                {status.open && <span className="availability-dot-ring" />}
            </span>
            {status.label}
        </span>
    )
}
